import Checkbox from '@components/checkbox';
import { Contact, ContainerSize, Settings } from '@lib/types';
import Container from '@components/container';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import countries from '@lib/countries.json';
import Input from '@components/input';
import Select from '@components/select';
import SubmitButton from '@components/submit-button';
import Textarea from '@components/textarea';
import { useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

type ContactProps = {
  data: Contact;
  settings: Partial<Settings>;
};

const formSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().email('Invalid email').min(1, 'Email is required'),
    jobTitle: z.string().min(1, 'Job title is required'),
    hospital: z.string().min(1, 'Hospital is required'),
    func: z.string().min(1, 'Function is required'),
    surgicalSpecialty: z.string(),
    country: z.string().min(1, 'Country is required'),
    message: z.string().min(1, 'Message is required'),
    newsletter: z.boolean(),
  })
  .refine(
    (data) => !(data.func === 'Surgery' && data.surgicalSpecialty.length < 1),
    {
      message: 'Surgical specialty is required',
      path: ['surgicalSpecialty'],
    },
  );

type FormSchemaType = z.infer<typeof formSchema>;

export default function ContactSection({
  data: {
    id,
    title,
    paragraph,
    successMsg,
    errorMsg,
    showBg,
    function: functionOptions,
    surgicalSpecialty: surgicalSpecialtyOptions,
  },
  settings,
}: ContactProps) {
  const [submitState, setSubmitState] =
    useState<'initial' | 'success' | 'error'>('initial');

  const {
    control,
    formState: { dirtyFields, errors, isSubmitting },
    handleSubmit,
    register,
    watch,
  } = useForm<FormSchemaType>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      jobTitle: '',
      hospital: '',
      func: '',
      surgicalSpecialty: '',
      message: '',
      country: '',
      newsletter: false,
    },
    mode: 'onChange',
    resolver: zodResolver(formSchema),
  });

  const funcField = watch('func');

  const onSubmit: SubmitHandler<FormSchemaType> = async (data) => {
    const [response] = await Promise.all([
      fetch('/api/pardot-proxy', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
      // wait at least 2000ms to show loading indicator
      new Promise((resolve) => setTimeout(resolve, 2000)),
    ]);

    if (response.ok) {
      return setSubmitState('success');
    }

    setSubmitState('error');
  };

  return (
    <Container size={ContainerSize.wide}>
      <div className="grid grid-cols-12 gap-1.5 md:gap-6 relative">
        <div
          className={`col-start-2 col-span-10 md:col-start-4 md:col-span-6 space-y-10 ${
            showBg ? 'py-20' : ''
          }`}>
          <h1 className="text-32 md:text-35 leading-130 font-bold text-blue-800">
            {title}
          </h1>
          <div
            className="prose"
            dangerouslySetInnerHTML={{ __html: paragraph }}
          />
          <form className="space-y-8" onSubmit={handleSubmit(onSubmit)}>
            <Input
              error={errors['firstName']?.message}
              label={'First name*'}
              id="firstName"
              isDirty={dirtyFields['firstName']}
              {...register('firstName')}
              type="text"
            />
            <Input
              error={errors['lastName']?.message}
              label={settings.texts?.placeholderContactName ?? 'Last name*'}
              id="lastName"
              isDirty={dirtyFields['lastName']}
              {...register('lastName')}
              type="text"
            />
            <Input
              error={errors['email']?.message}
              label={
                settings.texts?.placeholderContactEmail ?? 'Email address*'
              }
              id="email"
              isDirty={dirtyFields['email']}
              {...register('email')}
              type="email"
            />
            <Input
              error={errors['jobTitle']?.message}
              label={'Job title*'}
              id="jobTitle"
              isDirty={dirtyFields['jobTitle']}
              {...register('jobTitle')}
              type="text"
            />
            <Input
              error={errors['hospital']?.message}
              label={'Hospital*'}
              id="hospital"
              isDirty={dirtyFields['hospital']}
              {...register('hospital')}
              type="text"
            />
            <Controller
              render={({ field }) => {
                return (
                  <Select
                    error={errors['func']?.message}
                    id="func"
                    label="Function*"
                    options={functionOptions}
                    selected={field.value}
                    onChange={field.onChange}
                  />
                );
              }}
              control={control}
              name="func"
            />
            {funcField === 'Surgery' && (
              <Controller
                render={({ field }) => {
                  return (
                    <Select
                      error={errors['surgicalSpecialty']?.message}
                      id="surgicalSpecialty"
                      label="Specialty*"
                      options={surgicalSpecialtyOptions}
                      selected={field.value}
                      onChange={field.onChange}
                    />
                  );
                }}
                control={control}
                name="surgicalSpecialty"
              />
            )}
            <Controller
              render={({ field }) => {
                return (
                  <Select
                    error={errors['country']?.message}
                    id="country"
                    label="Country*"
                    options={countries}
                    selected={field.value}
                    onChange={field.onChange}
                  />
                );
              }}
              control={control}
              name="country"
            />
            <Textarea
              error={errors['message']?.message}
              label={settings.texts?.labelContactMessage ?? 'Message*'}
              id="message"
              isDirty={dirtyFields['message']}
              {...register('message')}
              placeholder={
                settings.texts?.placeholderContactMessage ??
                'Please enter your message.'
              }
              rows={8}
            />
            <Checkbox
              error={errors['newsletter']?.message}
              id="newsletter"
              label="Would you like to stay updated and receive email updates?"
              isDirty={dirtyFields['newsletter']}
              {...register('newsletter')}
            />
            <SubmitButton
              disabled={isSubmitting || submitState === 'success'}
              isSubmitting={isSubmitting}
              endMessage="Message sent!">
              {submitState === 'success'
                ? 'Message sent!'
                : settings.texts?.valueContactSend ?? 'Send Message'}
            </SubmitButton>
          </form>
          {submitState === 'error' && (
            <div
              className="prose text-error"
              dangerouslySetInnerHTML={{ __html: errorMsg }}
            />
          )}
          {submitState === 'success' && (
            <>
              <div
                className="prose"
                dangerouslySetInnerHTML={{ __html: successMsg }}
              />
            </>
          )}
        </div>
        {showBg && (
          <div className="absolute inset-0 z-[-1]">
            <div className="grid grid-cols-12 gap-1.5 md:gap-6 h-full">
              <div className="col-span-12 md:col-start-2 md:col-span-11 bg-blue-200 h-full rounded-xl md:rounded-3xl" />
            </div>
          </div>
        )}
      </div>
    </Container>
  );
}
